/* eslint-disable max-len */
import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ButtonSize } from 'components/UI_kit_3/Button/Button';
import { ReactComponent as ArrowIcon } from 'assets/icons_3/arrows/chevron-down.svg';
import { ReactComponent as CloseIcon } from 'assets/icons_3/x-close.svg';
import FormLabel from 'components/UI_kit_3/FormLabel/FormLabel';
import FormText from 'components/UI_kit_3/FormText/FormText';
import { mergeRefs } from 'utils/set-ref';
import OverlayTrigger, { Position } from 'components/UI_kit_3/Popover/OverlayTrigger/OverlayTrigger';
import Badge from 'components/UI_kit_3/Badge/Badge';
import FormCheck from 'components/UI_kit_3/FormCheck/FormCheck';
import styles from './DropdownSelector.module.scss';

export type DropdownSelectorData = {
  value: string | number | null;
  label: string | React.ReactNode;
  subTitle?: string;
  disabled?: boolean;
  badge?: string
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
};

type SingleProps = {
  value: number | string | null
  onChange: (value: number | string | null) => unknown
  multiple?: false
};

type MultipleProps = {
  value: Array<number | string>
  onChange: (value: Array<number | string>) => unknown
  multiple: true
};

type DropDownSelectorProps = {
  data: Array<DropdownSelectorData>;
  autoClose?: boolean;
  label?: string;
  error?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  required?: boolean;
  isValid?: boolean;
  size?: ButtonSize;
  id?: string;
  qaElement?: string;
  qaElementFeedback?: string;
  useOverlay?: boolean;
  overlayPosition?: Position;
  overlayOffset?: number;
  selectAll?: boolean;
  selectAllText?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  showCount?: boolean;
  resetChecked?: boolean;
  onResetChecked?: ()=>void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'value' | 'onChange' | 'data'> & (SingleProps | MultipleProps);

const DropdownSelector = forwardRef<HTMLDivElement, DropDownSelectorProps>(({
  className,
  value,
  data,
  label,
  error,
  isInvalid,
  isValid,
  size = 'md',
  placeholder,
  multiple,
  required,
  disabled,
  onChange,
  qaElement,
  qaElementFeedback,
  useOverlay = false,
  overlayPosition = 'bottom-left',
  overlayOffset = 4,
  selectAll = false,
  selectAllText,
  searchable = false,
  searchPlaceholder,
  showCount = false,
  resetChecked = false,
  onResetChecked,
  ...props
}, ref) => {
  const [t] = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  // should menu open upwards in case of overflowing bottom edge of the screen
  const [openUp, setOpenUp] = useState(false);
  const [search, setSearch] = useState<string>('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const toggleRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLUListElement>(null);

  const currentValue = React.useMemo(
    () => {
      if (multiple) {
        return data.filter((item) => item.value && value
          .includes(item.value));
      }
      return data.find((item) => item.value === value);
    },
    [value, data],
  );

  const handleSelect = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, itemValue: string | number | null) => {
    e.stopPropagation();
    if (multiple) {
      if ((value.length === 0) && itemValue) {
        onChange([itemValue]);
        return;
      }
      if (itemValue && value.includes(itemValue)) {
        onChange(value.filter((id) => id !== itemValue));
        return;
      }
      if (itemValue) {
        onChange([...value, itemValue]);
      }
      return;
    }
    onChange(itemValue);
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen((val) => !val);
  };

  const handleSelectAll = () => {
    if (!multiple) return;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const selectableValues = filteredData
      .filter((it) => it.value !== null && !it.disabled)
      .map((it) => it.value as number | string);

    const allSelected = selectableValues.length > 0
      && selectableValues.every((v) => (value as Array<number | string>).includes(v));

    if (allSelected) {
      // remove only those values that are in selectableValues
      onChange((value as Array<number | string>).filter((v) => !selectableValues.includes(v)));
    } else {
      // eslint-disable-next-line max-len
      const merged = Array.from(new Set([...(value as Array<number | string>), ...selectableValues]));
      onChange(merged);
    }
  };

  const handleClickOutside: EventListener = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.target as HTMLElement)) {
      setOpen(false);
    }
  };

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (searchable || multiple) {
      return () => {};
    }
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [searchable]);

  // close the menu if the dropdown becomes unavailable while it is open
  useEffect(() => {
    if (open && (disabled || data.length === 0)) {
      setOpen(false);
    }
  }, [disabled, data.length]);

  useEffect(() => {
    if (menuRef.current && open) {
      menuRef.current.childNodes.forEach((elem) => {
        const element = elem as HTMLElement;
        const attrValue = element.getAttribute('data-value');
        if (attrValue === value?.toString()) {
          (elem as HTMLElement).scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
        }
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const toggle = toggleRef.current;
    const menu = menuRef.current;
    if (!toggle || !menu) return;

    const toggleRect = toggle.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;

    const spaceBelow = window.innerHeight - toggleRect.bottom;
    const spaceAbove = toggleRect.top;

    const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > menuHeight;

    setOpenUp(shouldOpenUp);
  }, [open]);

  const filteredData = React.useMemo(() => {
    if (!search) return data;
    const q = search.trim().toLowerCase();
    return data.filter((it) => (
      (it.label || '').toString().toLowerCase().includes(q)
      || (it.subTitle || '').toString().toLowerCase().includes(q)
    ));
  }, [data, search]);

  const renderMenu = () => (
    <ul
      ref={menuRef}
      className={classNames(styles.menu, { [styles.menuOverlay]: open && useOverlay, [styles.menuPadding]: resetChecked })}
    >
      {searchable && (
        <li className={styles.searchItem} key="search">
          <FormText
            placeholder={searchPlaceholder || t('common.search')}
            value={search}
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
            qaElement="dropdown-search"
          />
        </li>
      )}

      {multiple && selectAll && !search && (
        (() => {
          const selectableValues = filteredData
            .filter((it) => it.value !== null && !it.disabled)
            .map((it) => it.value as number | string);
          const allSelected = selectableValues.length > 0
            && selectableValues.every((v) => (value as Array<number | string>).includes(v));
          return (
            <li
              className={classNames(styles.item, { [styles.hasCheckbox]: !!multiple })}
              key="select_all"
              data-value="select_all"
            >
              <button type="button" onClick={handleSelectAll}>
                {multiple && <FormCheck className={styles.check} type="checkbox" size="sm" checked={allSelected} readOnly />}
                <span>{ selectAllText || t('common.all')}</span>
              </button>
            </li>
          );
        })()
      )}
      {filteredData.length === 0 && search.length !== 0 && (
        <li className={classNames(styles.item, styles.noResults)} key="no_results">
          <div qa-element="nothing-found">{t('common.nothing_found')}</div>
        </li>
      )}

      {filteredData.map((item, index) => (
        <li
          className={classNames(
            styles.item,
            {
              [styles.itemActive]: (multiple && item.value)
                ? value.includes(item.value) : item.value === value,
              [styles.itemHasBadge]: !!item.badge,
              [styles.hasCheckbox]: !!multiple,
            },
          )}
          key={item.value}
          data-value={item.value}
        >
          <button type="button" onClick={(e) => handleSelect(e, item.value)} disabled={item.disabled} qa-element={`${qaElement}-${index}`}>
            {multiple && <FormCheck className={styles.check} type="checkbox" size="sm" checked={value.includes(item.value!)} readOnly disabled={item.disabled} />}
            {item.prefixIcon ? item.prefixIcon : null}
            <span className={styles.itemLabel}>{item.label}</span>
            <span className={styles.subTitle}>{item.subTitle}</span>
            {item.suffixIcon ? item.suffixIcon : null}
            {item.badge && <Badge variant="gray" size="sm" className={styles.badge}>{item.badge}</Badge>}
          </button>
        </li>
      ))}
      {resetChecked && (
        <li className={styles.resetChecked}>
          <button type="button" onClick={onResetChecked}>
            <CloseIcon />
            {t('common.reset')}
          </button>
        </li>
      )}
    </ul>
  );

  const renderToggle = () => (
    <button
      ref={toggleRef}
      type="button"
      className={classNames(styles.toggle, styles.input, styles[size])}
      onClick={handleOpen}
      disabled={disabled || data.length === 0}
      qa-element={`${qaElement}-toggle`}
    >
      <span className={classNames('flex align-items-center justify-content-between', { [styles.hasCount]: Array.isArray(currentValue) && currentValue?.length > 0 && showCount })}>
        {Array.isArray(currentValue) && currentValue.length > 0 && !showCount && currentValue.map((item) => item.label).join(', ')}
        {Array.isArray(currentValue) && currentValue.length > 0 && showCount && (
        <>
          <span>{(placeholder || t('common.select'))}</span>
          <Badge variant="primary" size="sm" pill>{currentValue.length}</Badge>
        </>
        )}
        {Array.isArray(currentValue) && currentValue.length === 0 && (placeholder || t('common.select'))}
        {!Array.isArray(currentValue) && (currentValue?.label || placeholder || t('common.select'))}
        {!Array.isArray(currentValue) && currentValue?.badge && <Badge variant="gray" size="sm">{currentValue.badge}</Badge>}
      </span>
      {(Array.isArray(currentValue) && currentValue.length > 0 && showCount) || <ArrowIcon className={classNames(styles.arrow, { [styles.transform]: open })} />}
    </button>
  );

  return (
    <div
      className={classNames(styles.dropdown, styles.formText, className, {
        [styles.open]: open && !useOverlay,
        [styles.invalid]: isInvalid,
        [styles.valid]: isValid,
        [styles.lg]: size === 'lg',
        [styles.sm]: size === 'sm',
        [styles.withOverlay]: open && useOverlay,
        [styles.openUp]: openUp && !useOverlay,
      })}
      {...props}
      ref={mergeRefs(ref, containerRef)}
      // eslint-disable-next-line react/no-unknown-property
      qa-element={qaElement}
    >
      {label && (
        <FormLabel required={required} className={styles.label} htmlFor={props.id}>
          {label}
        </FormLabel>
      )}

      {useOverlay ? (
        <OverlayTrigger
          trigger="click"
          position={overlayPosition}
          offset={overlayOffset}
          overlay={renderMenu()}
          open={open}
          onOpenChange={setOpen}
        >
          {renderToggle()}
        </OverlayTrigger>
      ) : (
        <>
          {renderToggle()}
          {renderMenu()}
        </>
      )}

      {error && isInvalid && <div className={classNames(styles.feedback)} qa-element={qaElementFeedback}>{error}</div>}
    </div>
  );
});

export default DropdownSelector;
